<template>
  <div class="loading-state">
    <!-- Skeleton Loading -->
    <div v-if="type === 'skeleton'" class="skeleton-container">
      <v-skeleton-loader
        :type="skeletonType"
        :loading="loading"
        class="mx-auto"
      >
        <slot />
      </v-skeleton-loader>
    </div>

    <!-- Spinner Loading -->
    <div v-else-if="type === 'spinner'" class="spinner-container text-center pa-8">
      <v-progress-circular
        :indeterminate="loading"
        :size="size"
        :width="width"
        :color="color"
      />
      <div v-if="message" class="text-h6 mt-4">{{ message }}</div>
      <div v-if="subMessage" class="text-body-2 grey--text mt-2">{{ subMessage }}</div>
    </div>

    <!-- Linear Progress -->
    <div v-else-if="type === 'linear'" class="linear-container">
      <v-progress-linear
        :indeterminate="loading && !progress"
        :value="progress"
        :color="color"
        :height="height"
        :striped="striped"
      />
      <div v-if="message" class="text-center mt-2">
        <div class="text-subtitle-2">{{ message }}</div>
        <div v-if="progress" class="text-caption grey--text">
          {{ Math.round(progress) }}% complete
        </div>
      </div>
    </div>

    <!-- Card Loading -->
    <div v-else-if="type === 'card'" class="card-container">
      <v-card :loading="loading" :disabled="loading">
        <v-card-text class="text-center pa-8">
          <v-progress-circular
            v-if="loading"
            :indeterminate="true"
            :size="size"
            :color="color"
          />
          <div v-if="loading && message" class="text-h6 mt-4">{{ message }}</div>
          <div v-if="loading && subMessage" class="text-body-2 grey--text mt-2">
            {{ subMessage }}
          </div>
          <slot v-if="!loading" />
        </v-card-text>
      </v-card>
    </div>

    <!-- Overlay Loading -->
    <div v-else-if="type === 'overlay'" class="overlay-container position-relative">
      <v-overlay :value="loading" absolute>
        <v-progress-circular
          :indeterminate="true"
          :size="size"
          :color="color"
        />
        <div v-if="message" class="text-center mt-4">
          <div class="text-h6 white--text">{{ message }}</div>
          <div v-if="subMessage" class="text-body-2 grey--text lighten-2 mt-2">
            {{ subMessage }}
          </div>
        </div>
      </v-overlay>
      <slot />
    </div>

    <!-- Default Spinner -->
    <div v-else class="default-container text-center pa-4">
      <v-progress-circular
        v-if="loading"
        :indeterminate="true"
        :size="size"
        :color="color"
      />
      <div v-if="loading && message" class="text-subtitle-1 mt-2">{{ message }}</div>
      <slot v-if="!loading" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoadingState',

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'spinner',
      validator: (value) => ['skeleton', 'spinner', 'linear', 'card', 'overlay', 'default'].includes(value),
    },
    message: {
      type: String,
      default: '',
    },
    subMessage: {
      type: String,
      default: '',
    },
    size: {
      type: [Number, String],
      default: 64,
    },
    width: {
      type: [Number, String],
      default: 4,
    },
    height: {
      type: [Number, String],
      default: 4,
    },
    color: {
      type: String,
      default: 'primary',
    },
    progress: {
      type: Number,
      default: null,
    },
    striped: {
      type: Boolean,
      default: false,
    },
    skeletonType: {
      type: String,
      default: 'card',
    },
  },
};
</script>

<style scoped>
.loading-state {
  width: 100%;
}

.skeleton-container,
.spinner-container,
.linear-container,
.card-container,
.overlay-container,
.default-container {
  width: 100%;
}

.position-relative {
  position: relative;
}
</style>
